import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Info, Lock, Play, RotateCcw, Square, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  CHESTER_PROTOCOL_METADATA,
  buildChesterPayload,
  getDefaultChesterLevels,
  normalizeChesterPayload,
  resolveChesterInterpretation,
  summarizeChesterResult,
  type ChesterLevelResult,
  type ChesterResultPayload,
  type ChesterTerminationReason,
  type StepTestTimerBlock,
  type StepTestVitalsBlock,
} from '../step-tests/stepTestsDomain';
import { useMetronome } from './useMetronome';

type PatientContextInput = {
  birthDate?: string | null;
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
  identifier?: string | null;
};

interface ChesterStepTestProps {
  initial?: ChesterResultPayload | null;
  isEditing?: boolean;
  patientContext?: PatientContextInput;
  onCancel?: () => void;
  onNext: (payload: ChesterResultPayload) => void;
}

type VitalsFormState = {
  hr: string;
  spo2: string;
  bpSys: string;
  bpDia: string;
  borgDyspnea: number | null;
  borgLegFatigue: number | null;
};

type LevelFormState = {
  level: 1 | 2 | 3 | 4 | 5;
  cadenceStepsPerMinute: number;
  durationSeconds: number;
  completed: boolean;
  hr: string;
  spo2: string;
  borgDyspnea: number | null;
  borgLegFatigue: number | null;
  totalSteps: string;
  notes: string;
};

export type { ChesterResultPayload };

const BORG_VALUES: number[] = [0, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const CHESTER_LEVEL_DURATION_SECONDS = 120;
const CHESTER_TOTAL_DURATION_SECONDS = 600;

const TERMINATION_OPTIONS: Array<{ value: ChesterTerminationReason; label: string }> = [
  { value: 'completed_all_levels', label: 'Completou todos os níveis' },
  { value: 'symptom_limitation', label: 'Limitação por sintomas' },
  { value: 'pace_loss', label: 'Perda da cadência' },
  { value: 'hr_limit', label: 'Atingiu FC limite' },
  { value: 'supervisor_interruption', label: 'Interrupção do avaliador' },
  { value: 'patient_request', label: 'Pedido do paciente' },
];

const buildVitalsState = (value?: StepTestVitalsBlock | null): VitalsFormState => ({
  hr: value?.hr ?? '',
  spo2: value?.spo2 ?? '',
  bpSys: value?.bp.sys ?? '',
  bpDia: value?.bp.dia ?? '',
  borgDyspnea: value?.borg.dyspnea ?? null,
  borgLegFatigue: value?.borg.legFatigue ?? null,
});

const calculateExpectedLevelSteps = (cadenceStepsPerMinute: number, durationSeconds = CHESTER_LEVEL_DURATION_SECONDS): number =>
  Math.round(cadenceStepsPerMinute * (durationSeconds / 60));

const calculatePartialLevelSteps = (cadenceStepsPerMinute: number, elapsedSeconds: number): number =>
  Math.max(0, Math.floor(cadenceStepsPerMinute * (Math.max(0, elapsedSeconds) / 60)));

const buildLevelState = (value?: ChesterLevelResult | null, fallback?: ChesterLevelResult): LevelFormState => {
  const cadenceStepsPerMinute = value?.cadenceStepsPerMinute ?? fallback?.cadenceStepsPerMinute ?? 15;
  const durationSeconds = value?.durationSeconds ?? fallback?.durationSeconds ?? CHESTER_LEVEL_DURATION_SECONDS;
  const completed = value?.completed ?? false;

  return {
    level: value?.level ?? fallback?.level ?? 1,
    cadenceStepsPerMinute,
    durationSeconds,
    completed,
    hr: value?.hr ?? '',
    spo2: value?.spo2 ?? '',
    borgDyspnea: value?.borgDyspnea ?? null,
    borgLegFatigue: value?.borgLegFatigue ?? null,
    totalSteps: completed
      ? String(calculateExpectedLevelSteps(cadenceStepsPerMinute, durationSeconds))
      : value?.totalSteps != null
        ? String(value.totalSteps)
        : '',
    notes: value?.notes ?? '',
  };
};

const synchronizeLevelsWithElapsedTime = (levels: LevelFormState[], elapsedSeconds: number): LevelFormState[] =>
  levels.map((entry, index) => {
    const levelStart = index * entry.durationSeconds;
    const levelEnd = levelStart + entry.durationSeconds;
    const completed = elapsedSeconds >= levelEnd;
    const isPartialLevel = elapsedSeconds > levelStart && elapsedSeconds < levelEnd;
    const nextSteps = completed
      ? String(calculateExpectedLevelSteps(entry.cadenceStepsPerMinute, entry.durationSeconds))
      : isPartialLevel
        ? String(calculatePartialLevelSteps(entry.cadenceStepsPerMinute, elapsedSeconds - levelStart))
        : '';

    if (entry.completed === completed && entry.totalSteps === nextSteps) return entry;
    return { ...entry, completed, totalSteps: nextSteps };
  });

const parseNullableNumber = (value: string): number | null => {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseInteger = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatClock = (seconds: number): string => {
  const safe = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const toVitalsPayload = (state: VitalsFormState): StepTestVitalsBlock => ({
  hr: state.hr.trim() || null,
  spo2: state.spo2.trim() || null,
  bp: {
    sys: state.bpSys.trim() || null,
    dia: state.bpDia.trim() || null,
  },
  borg: {
    dyspnea: state.borgDyspnea,
    legFatigue: state.borgLegFatigue,
  },
});

const MetricCard = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

function BorgSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  return (
    <label className="space-y-1">
      <div className="text-xs font-medium text-slate-600">{label}</div>
      <select
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
        value={value == null ? '' : String(value)}
        onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
      >
        <option value="">Não informado</option>
        {BORG_VALUES.map((item) => (
          <option key={`${label}-${item}`} value={item}>
            {String(item).replace('.', ',')}
          </option>
        ))}
      </select>
    </label>
  );
}

function VitalsEditor({
  title,
  state,
  onChange,
}: {
  title: string;
  state: VitalsFormState;
  onChange: (patch: Partial<VitalsFormState>) => void;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 text-xs font-black uppercase tracking-[0.24em] text-slate-400">{title}</div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">FC (bpm)</div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={state.hr} onChange={(event) => onChange({ hr: event.target.value })} />
        </label>
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">SpO2 (%)</div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={state.spo2} onChange={(event) => onChange({ spo2: event.target.value })} />
        </label>
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">PAS (mmHg)</div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={state.bpSys} onChange={(event) => onChange({ bpSys: event.target.value })} />
        </label>
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">PAD (mmHg)</div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={state.bpDia} onChange={(event) => onChange({ bpDia: event.target.value })} />
        </label>
        <BorgSelect label="Borg dispneia" value={state.borgDyspnea} onChange={(value) => onChange({ borgDyspnea: value })} />
        <BorgSelect label="Borg fadiga de MMII" value={state.borgLegFatigue} onChange={(value) => onChange({ borgLegFatigue: value })} />
      </div>
    </div>
  );
}

export default function ChesterStepTest({ initial = null, isEditing = false, patientContext, onCancel, onNext }: ChesterStepTestProps) {
  const normalizedInitial = normalizeChesterPayload(initial);
  const fallbackLevels = getDefaultChesterLevels();
  const [baseline, setBaseline] = useState<VitalsFormState>(buildVitalsState(normalizedInitial?.baseline));
  const [finalBlock, setFinalBlock] = useState<VitalsFormState>(buildVitalsState(normalizedInitial?.final));
  const [levels, setLevels] = useState<LevelFormState[]>(
    fallbackLevels.map((fallback, index) => buildLevelState(normalizedInitial?.levels?.[index], fallback)),
  );
  const [notes, setNotes] = useState(normalizedInitial?.notes ?? '');
  const [stepHeightCm, setStepHeightCm] = useState(String(normalizedInitial?.protocol.stepHeightCm ?? 17));
  const [terminationReason, setTerminationReason] = useState<ChesterTerminationReason | ''>(normalizedInitial?.terminationReason ?? '');

  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(normalizedInitial?.timer.elapsed_s ?? 0);
  const [startedAt, setStartedAt] = useState<string | null>(normalizedInitial?.timer.startedAt ?? null);
  const [endedAt, setEndedAt] = useState<string | null>(normalizedInitial?.timer.endedAt ?? null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const timerStartedAtRef = useRef<number | null>(null);
  const accumulatedSecondsRef = useRef<number>(normalizedInitial?.timer.elapsed_s ?? 0);
  const timerIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!timerRunning) {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    timerIntervalRef.current = window.setInterval(() => {
      const started = timerStartedAtRef.current;
      if (started == null) return;
      const nextElapsed = accumulatedSecondsRef.current + Math.floor((Date.now() - started) / 1000);
      if (nextElapsed >= CHESTER_TOTAL_DURATION_SECONDS) {
        accumulatedSecondsRef.current = CHESTER_TOTAL_DURATION_SECONDS;
        setElapsedSeconds(CHESTER_TOTAL_DURATION_SECONDS);
        timerStartedAtRef.current = null;
        setTimerRunning(false);
        setEndedAt(new Date().toISOString());
        setTerminationReason('completed_all_levels');
        return;
      }
      setElapsedSeconds(nextElapsed);
    }, 250);

    return () => {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [timerRunning]);

  useEffect(() => {
    if (isEditing) return;
    setLevels((current) => synchronizeLevelsWithElapsedTime(current, elapsedSeconds));
  }, [elapsedSeconds, isEditing]);

  const totalSteps = useMemo(
    () => levels.reduce((sum, item) => sum + (parseInteger(item.totalSteps) ?? 0), 0),
    [levels],
  );
  const completedLevels = useMemo(() => levels.filter((item) => item.completed).length, [levels]);
  const currentLevelIndex = Math.min(4, Math.floor(elapsedSeconds / CHESTER_LEVEL_DURATION_SECONDS));
  const partialLevelIndex = useMemo(() => {
    const recordedPartialIndex = levels.findIndex((entry) => !entry.completed && parseInteger(entry.totalSteps) != null);
    if (recordedPartialIndex >= 0) return recordedPartialIndex;
    if (elapsedSeconds > 0 && elapsedSeconds < CHESTER_TOTAL_DURATION_SECONDS && elapsedSeconds % CHESTER_LEVEL_DURATION_SECONDS !== 0) {
      return Math.floor(elapsedSeconds / CHESTER_LEVEL_DURATION_SECONDS);
    }
    return null;
  }, [elapsedSeconds, levels]);
  const displayLevelIndex = isEditing ? Math.min(4, partialLevelIndex ?? completedLevels) : currentLevelIndex;
  const currentCadence = levels[displayLevelIndex]?.cadenceStepsPerMinute ?? 35;
  const testEnded = Boolean(endedAt) || elapsedSeconds >= CHESTER_TOTAL_DURATION_SECONDS;
  const metronomeBpm = currentCadence * 4;
  const { ensureContext } = useMetronome({
    enabled: timerRunning,
    bpm: metronomeBpm,
    accentEvery: 4,
    muted: !soundEnabled,
  });

  const payloadPreview = useMemo(() => {
    const levelPayload: ChesterLevelResult[] = levels.map((entry) => ({
      level: entry.level,
      cadenceStepsPerMinute: entry.cadenceStepsPerMinute,
      durationSeconds: entry.durationSeconds,
      completed: entry.completed,
      hr: entry.hr.trim() || null,
      spo2: entry.spo2.trim() || null,
      borgDyspnea: entry.borgDyspnea,
      borgLegFatigue: entry.borgLegFatigue,
      totalSteps: parseInteger(entry.totalSteps),
      notes: entry.notes.trim() || null,
    }));
    const timer: StepTestTimerBlock = {
      elapsed_s: elapsedSeconds,
      completed: elapsedSeconds >= CHESTER_TOTAL_DURATION_SECONDS,
      startedAt,
      endedAt,
    };
    const payload = buildChesterPayload({
      protocol: {
        stepHeightCm: parseNullableNumber(stepHeightCm) ?? 17,
      },
      baseline: toVitalsPayload(baseline),
      levels: levelPayload,
      final: toVitalsPayload(finalBlock),
      totalSteps,
      achievedLevel: completedLevels,
      terminationReason: completedLevels === 5 ? 'completed_all_levels' : terminationReason || 'patient_request',
      completedAllLevels: completedLevels === 5,
      timer,
      notes,
      patientContext: patientContext ? { ...patientContext } : undefined,
      finishedAt: endedAt ?? new Date().toISOString(),
    });
    payload.report = {
      ...(payload.report ?? {}),
      interpretation: resolveChesterInterpretation(payload),
      referenceSource: CHESTER_PROTOCOL_METADATA.source,
    };
    return payload;
  }, [baseline, completedLevels, elapsedSeconds, endedAt, finalBlock, levels, notes, patientContext, startedAt, stepHeightCm, terminationReason, totalSteps]);

  const summary = useMemo(() => summarizeChesterResult(payloadPreview), [payloadPreview]);

  const updateLevel = (level: number, patch: Partial<LevelFormState>) => {
    setLevels((current) => current.map((entry) => (entry.level === level ? { ...entry, ...patch } : entry)));
  };

  const handleStart = async () => {
    if (isEditing || timerRunning || testEnded || elapsedSeconds > 0) return;
    if (soundEnabled) {
      await ensureContext();
    }
    if (!startedAt) setStartedAt(new Date().toISOString());
    timerStartedAtRef.current = Date.now();
    setTimerRunning(true);
  };

  const handleStop = () => {
    if (!timerRunning) return;
    const started = timerStartedAtRef.current;
    const nextElapsed = started == null ? accumulatedSecondsRef.current : accumulatedSecondsRef.current + Math.floor((Date.now() - started) / 1000);
    const safeElapsed = Math.min(Math.max(1, nextElapsed), CHESTER_TOTAL_DURATION_SECONDS);
    accumulatedSecondsRef.current = safeElapsed;
    setElapsedSeconds(safeElapsed);
    setLevels((current) => synchronizeLevelsWithElapsedTime(current, safeElapsed));
    timerStartedAtRef.current = null;
    setTimerRunning(false);
    setEndedAt(new Date().toISOString());
  };

  const handleToggleSound = async () => {
    const nextEnabled = !soundEnabled;
    setSoundEnabled(nextEnabled);
    if (nextEnabled && timerRunning) {
      await ensureContext();
    }
  };

  const handleResetTimer = () => {
    if (isEditing || timerRunning) return;
    accumulatedSecondsRef.current = 0;
    timerStartedAtRef.current = null;
    setTimerRunning(false);
    setElapsedSeconds(0);
    setStartedAt(null);
    setEndedAt(null);
    setTerminationReason('');
    setLevels((current) =>
      current.map((entry) => ({
        ...entry,
        completed: false,
        hr: '',
        spo2: '',
        borgDyspnea: null,
        borgLegFatigue: null,
        totalSteps: '',
        notes: '',
      })),
    );
  };

  const handleEditCompletedLevels = (nextCompletedLevels: number) => {
    if (!isEditing) return;
    const safeCompletedLevels = Math.max(0, Math.min(5, Math.round(nextCompletedLevels)));
    const nextElapsedSeconds = safeCompletedLevels * CHESTER_LEVEL_DURATION_SECONDS;
    setElapsedSeconds(nextElapsedSeconds);
    accumulatedSecondsRef.current = nextElapsedSeconds;
    setLevels((current) => synchronizeLevelsWithElapsedTime(current, nextElapsedSeconds));
    if (safeCompletedLevels === 5) {
      setTerminationReason('completed_all_levels');
      return;
    }
    if (terminationReason === 'completed_all_levels') setTerminationReason('');
  };

  const handleEditElapsedSeconds = (value: string) => {
    if (!isEditing) return;
    const parsed = Number.parseInt(value, 10);
    const safeElapsed = Number.isFinite(parsed) ? Math.max(0, Math.min(CHESTER_TOTAL_DURATION_SECONDS, parsed)) : 0;
    setElapsedSeconds(safeElapsed);
    accumulatedSecondsRef.current = safeElapsed;
    setLevels((current) => synchronizeLevelsWithElapsedTime(current, safeElapsed));
    if (safeElapsed >= CHESTER_TOTAL_DURATION_SECONDS) setTerminationReason('completed_all_levels');
    else if (terminationReason === 'completed_all_levels') setTerminationReason('');
  };

  const handleFinish = () => {
    if (timerRunning) {
      toast.error('Encerre o teste antes de salvar o resultado.');
      return;
    }
    if (completedLevels === 0 && totalSteps <= 0) {
      toast.error('O Chester precisa conter ao menos um nível iniciado para ser salvo.');
      return;
    }
    if (completedLevels < 5 && (!terminationReason || terminationReason === 'completed_all_levels')) {
      toast.error('Informe o motivo de encerramento do teste.');
      return;
    }
    const started = timerStartedAtRef.current;
    const nextElapsed =
      timerRunning && started != null ? accumulatedSecondsRef.current + Math.floor((Date.now() - started) / 1000) : elapsedSeconds;
    const safeElapsed = Math.min(nextElapsed, CHESTER_TOTAL_DURATION_SECONDS);
    if (timerRunning) {
      accumulatedSecondsRef.current = safeElapsed;
      setElapsedSeconds(safeElapsed);
      timerStartedAtRef.current = null;
      setTimerRunning(false);
    }
    const finishedAtIso = new Date().toISOString();
    setEndedAt(finishedAtIso);
    const finalPayload = buildChesterPayload({
      protocol: {
        stepHeightCm: parseNullableNumber(stepHeightCm) ?? 17,
      },
      baseline: toVitalsPayload(baseline),
      levels: levels.map((entry) => ({
        level: entry.level,
        cadenceStepsPerMinute: entry.cadenceStepsPerMinute,
        durationSeconds: entry.durationSeconds,
        completed: entry.completed,
        hr: entry.hr.trim() || null,
        spo2: entry.spo2.trim() || null,
        borgDyspnea: entry.borgDyspnea,
        borgLegFatigue: entry.borgLegFatigue,
        totalSteps: parseInteger(entry.totalSteps),
        notes: entry.notes.trim() || null,
      })),
      final: toVitalsPayload(finalBlock),
      totalSteps,
      achievedLevel: completedLevels,
      terminationReason: completedLevels === 5 ? 'completed_all_levels' : terminationReason || 'patient_request',
      completedAllLevels: completedLevels === 5,
      timer: {
        elapsed_s: safeElapsed,
        completed: safeElapsed >= CHESTER_TOTAL_DURATION_SECONDS,
        startedAt,
        endedAt: finishedAtIso,
      },
      notes,
      patientContext: patientContext ? { ...patientContext } : undefined,
      finishedAt: finishedAtIso,
    });
    finalPayload.report = {
      ...(finalPayload.report ?? {}),
      interpretation: resolveChesterInterpretation(finalPayload),
      referenceSource: CHESTER_PROTOCOL_METADATA.source,
    };
    onNext(finalPayload);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-[24px] border border-amber-200 bg-amber-50/80 p-5 text-amber-950">
        <Info className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="space-y-2 text-sm">
          <div className="font-semibold">Chester Step Test incremental</div>
          <div>Protocolo incremental com degrau de 17 cm, até 5 níveis e progressão de 15, 20, 25, 30 e 35 passos/min.</div>
          <div className="text-amber-900/80">{CHESTER_PROTOCOL_METADATA.description}</div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Tempo decorrido" value={formatClock(elapsedSeconds)} note={elapsedSeconds >= CHESTER_TOTAL_DURATION_SECONDS ? 'Tempo máximo atingido' : 'Máximo de 10 minutos'} />
        <MetricCard label={testEnded ? 'Último nível' : 'Nível atual'} value={`${Math.min(displayLevelIndex + 1, 5)}/5`} note={`Cadência alvo: ${currentCadence} passos/min`} />
        <MetricCard label="Níveis concluídos" value={String(completedLevels)} note={summary.severity} />
        <MetricCard label="Ciclos totais" value={String(totalSteps)} note="Subida e descida completas" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
        <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Execução</div>
              <div className="mt-1 text-lg font-bold text-slate-900">Cronômetro incremental e monitoramento por nível</div>
            </div>
            <div className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
              {isEditing ? 'Editando resultado' : elapsedSeconds >= CHESTER_TOTAL_DURATION_SECONDS ? 'Concluído' : timerRunning ? 'Em andamento' : testEnded ? 'Encerrado' : 'Aguardando início'}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[1fr_auto]">
            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Cronômetro</div>
              <div className="mt-2 text-5xl font-black tracking-tight text-slate-900">{formatClock(elapsedSeconds)}</div>
              <div className="mt-2 text-sm text-slate-600">
                Nível em destaque: {Math.min(displayLevelIndex + 1, 5)} • Cadência alvo {currentCadence} passos/min
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Sinal sonoro com 4 tempos por ciclo para acompanhar a subida e descida no degrau.
              </div>
              {isEditing ? (
                <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
                  Corrija abaixo o tempo, os níveis, os sinais e o motivo de encerramento. O cronômetro não será reiniciado.
                </div>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" onClick={handleStart} disabled={timerRunning || testEnded || elapsedSeconds > 0}>
                    <Play className="h-4 w-4" />
                    Iniciar
                  </button>
                  <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" onClick={handleStop} disabled={!timerRunning}>
                    <Square className="h-4 w-4" />
                    Encerrar teste
                  </button>
                  <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50" onClick={handleResetTimer} disabled={timerRunning}>
                    <RotateCcw className="h-4 w-4" />
                    Resetar
                  </button>
                  <button
                    type="button"
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
                      soundEnabled ? 'border border-emerald-200 bg-emerald-50 text-emerald-700' : 'border border-slate-200 text-slate-700'
                    }`}
                    onClick={handleToggleSound}
                  >
                    {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                    {soundEnabled ? 'Som ligado' : 'Som desligado'}
                  </button>
                </div>
              )}
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
              {isEditing && (
                <>
                  <label className="block space-y-1">
                    <div className="text-xs font-medium text-slate-600">Tempo realizado (segundos)</div>
                    <input type="number" min={0} max={CHESTER_TOTAL_DURATION_SECONDS} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={elapsedSeconds} onChange={(event) => handleEditElapsedSeconds(event.target.value)} />
                  </label>
                  <label className="mt-3 block space-y-1">
                    <div className="text-xs font-medium text-slate-600">Níveis completamente concluídos</div>
                    <select className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" value={completedLevels} onChange={(event) => handleEditCompletedLevels(Number(event.target.value))}>
                      {[0, 1, 2, 3, 4, 5].map((value) => <option key={`chester-completed-levels-${value}`} value={value}>{value}</option>)}
                    </select>
                  </label>
                </>
              )}
              <label className="space-y-1">
                <div className="text-xs font-medium text-slate-600">Altura do degrau (cm)</div>
                <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={stepHeightCm} onChange={(event) => setStepHeightCm(event.target.value)} />
              </label>
              <label className="mt-3 block space-y-1">
                <div className="text-xs font-medium text-slate-600">Motivo de encerramento</div>
                <select className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" value={completedLevels === 5 ? 'completed_all_levels' : terminationReason} disabled={completedLevels === 5} onChange={(event) => setTerminationReason(event.target.value as ChesterTerminationReason | '')}>
                  <option value="">Selecione o motivo</option>
                  {TERMINATION_OPTIONS.filter((option) => completedLevels === 5 || option.value !== 'completed_all_levels').map((option) => (
                    <option key={`chester-termination-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>

        <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Leitura automática</div>
            <div className="mt-1 text-lg font-bold text-slate-900">{summary.severity}</div>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <div className="font-semibold text-slate-900">Interpretação</div>
            <div className="mt-2 leading-relaxed">
              {completedLevels < 5 && !terminationReason
                ? 'Selecione o motivo de encerramento para concluir a leitura automática.'
                : resolveChesterInterpretation(payloadPreview)}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <div>FC pico: <span className="font-semibold text-slate-900">{summary.peakHr != null ? `${summary.peakHr.toFixed(0)} bpm` : '—'}</span></div>
            <div className="mt-1">SpO2 mínima: <span className="font-semibold text-slate-900">{summary.minSpo2 != null ? `${summary.minSpo2.toFixed(1)}%` : '—'}</span></div>
            <div className="mt-1">Maior cadência: <span className="font-semibold text-slate-900">{summary.maxCadence != null ? `${summary.maxCadence} passos/min` : '—'}</span></div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <VitalsEditor title="Repouso pré-teste" state={baseline} onChange={(patch) => setBaseline((current) => ({ ...current, ...patch }))} />
        <VitalsEditor title="Pós-teste / recuperação imediata" state={finalBlock} onChange={(patch) => setFinalBlock((current) => ({ ...current, ...patch }))} />
      </div>

      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Níveis do protocolo</div>
        <div className="mt-2 text-sm text-slate-600">
          Os ciclos dos níveis completos são calculados automaticamente. Somente o último nível interrompido permite correção manual.
        </div>
        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          {levels.map((entry, index) => {
            const isPartialLevel = partialLevelIndex === index;
            const hasRecordedData = Boolean(
              entry.hr ||
              entry.spo2 ||
              entry.borgDyspnea != null ||
              entry.borgLegFatigue != null ||
              entry.notes.trim(),
            );
            const isAwaitingFirstLevel = !startedAt && elapsedSeconds === 0 && index === 0;
            const isActiveLevel = timerRunning && index === currentLevelIndex;
            const isAvailable = entry.completed || isPartialLevel || isActiveLevel || isAwaitingFirstLevel || (isEditing && hasRecordedData);
            const expectedSteps = calculateExpectedLevelSteps(entry.cadenceStepsPerMinute, entry.durationSeconds);
            const statusLabel = entry.completed
              ? 'Concluído automaticamente'
              : isPartialLevel && testEnded
                ? 'Nível interrompido'
                : isActiveLevel
                  ? 'Em andamento'
                  : isAvailable
                    ? 'Próximo nível'
                    : 'Aguardando nível anterior';

            return (
              <div
                key={`chester-level-${entry.level}`}
                className={`rounded-[24px] border p-4 shadow-sm ${
                  isActiveLevel
                    ? 'border-amber-300 bg-amber-50/80'
                    : entry.completed
                      ? 'border-emerald-200 bg-emerald-50/40'
                      : isPartialLevel
                        ? 'border-sky-200 bg-sky-50/50'
                        : 'border-slate-200 bg-white'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Nível {entry.level}</div>
                    <div className="mt-1 text-lg font-bold text-slate-900">{entry.cadenceStepsPerMinute} passos/min</div>
                  </div>
                  <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
                    entry.completed
                      ? 'bg-emerald-100 text-emerald-700'
                      : isPartialLevel
                        ? 'bg-sky-100 text-sky-700'
                        : isActiveLevel
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-slate-100 text-slate-500'
                  }`}>
                    {entry.completed ? <CheckCircle2 className="h-4 w-4" /> : !isAvailable ? <Lock className="h-4 w-4" /> : null}
                    {statusLabel}
                  </div>
                </div>

                {isAvailable ? (
                  <>
                    <div className="mt-3 rounded-2xl border border-white/80 bg-white/80 px-3 py-2 text-xs text-slate-600">
                      {entry.completed
                        ? `${expectedSteps} ciclos calculados • 2 minutos completos`
                        : isPartialLevel
                          ? `${entry.totalSteps || '0'} ciclo(s) registrado(s) no nível interrompido`
                          : `Ao completar 2 minutos, o sistema registrará ${expectedSteps} ciclos.`}
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <label className="space-y-1">
                        <div className="text-xs font-medium text-slate-600">FC ao final do nível (bpm)</div>
                        <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={entry.hr} onChange={(event) => updateLevel(entry.level, { hr: event.target.value })} />
                      </label>
                      <label className="space-y-1">
                        <div className="text-xs font-medium text-slate-600">SpO2 ao final do nível (%)</div>
                        <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={entry.spo2} onChange={(event) => updateLevel(entry.level, { spo2: event.target.value })} />
                      </label>
                      {isPartialLevel && (
                        <label className="space-y-1">
                          <div className="text-xs font-medium text-slate-600">Ciclos concluídos no nível interrompido</div>
                          <input
                            type="number"
                            min={0}
                            max={expectedSteps}
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            value={entry.totalSteps}
                            disabled={timerRunning && !isEditing}
                            onChange={(event) => updateLevel(entry.level, { totalSteps: event.target.value })}
                          />
                          <div className="text-[11px] text-slate-500">Estimado pelo cronômetro; ajuste se a perda de cadência alterou a contagem real.</div>
                        </label>
                      )}
                      <div className="grid gap-3 md:grid-cols-2">
                        <BorgSelect label="Borg dispneia" value={entry.borgDyspnea} onChange={(value) => updateLevel(entry.level, { borgDyspnea: value })} />
                        <BorgSelect label="Borg MMII" value={entry.borgLegFatigue} onChange={(value) => updateLevel(entry.level, { borgLegFatigue: value })} />
                      </div>
                    </div>

                    <label className="mt-3 block space-y-1">
                      <div className="text-xs font-medium text-slate-600">Observações do nível</div>
                      <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={entry.notes} onChange={(event) => updateLevel(entry.level, { notes: event.target.value })} />
                    </label>
                  </>
                ) : (
                  <div className="mt-4 flex items-center gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
                    <Lock className="h-4 w-4" />
                    Os campos serão liberados quando este nível for alcançado.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Observações</div>
        <textarea
          className="mt-3 min-h-[110px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Descreva sintomas limitantes, dificuldade em acompanhar a cadência, necessidade de apoio ou intercorrências."
        />
      </div>

      <div className="flex flex-wrap justify-end gap-3">
        <button type="button" className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700" onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" className="rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm" onClick={handleFinish}>
          {isEditing ? 'Salvar alterações' : 'Salvar Chester'}
        </button>
      </div>
    </div>
  );
}
